import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  FaFileAlt, FaCalendarAlt, FaUser, FaDownload, FaSpinner, FaBuilding, FaChartLine,
  FaBoxes, FaChartBar, FaTimes, FaArrowRight, FaArrowLeft, FaTrash, FaPlus, FaMinus,
  FaCheckSquare, FaSquare, FaSearch, FaCog, FaEdit, FaCheckCircle, FaTimesCircle,
  FaIdCard, FaExclamationTriangle, FaMapMarkerAlt, FaPhone, FaEnvelope, FaSave, FaCheck
} from 'react-icons/fa';
import Header from '../others/Header';
import config from '../config/config';
import { useToast } from '../components/ToastContext';
import { decodeToken, isExpired } from 'react-jwt';
import InputMask from 'react-input-mask';
import { consultarCnpj, completarComDadosCnpj, avisoCompletarCnpj } from '../others/consultaCnpj';
import ImportarPrintCliente, { ESTILO_CAMPO_DO_PRINT, cepVeioDoPrint } from '../components/ImportarPrintCliente';

// ─── ItemImportavel fora do componente pai para evitar perda de foco ────────
// ── CORREÇÃO: ao marcar o checkbox, a importação do Painel CRVA acontece
// automaticamente — sem botão, sem texto explicando o processo. Se marcado e
// ainda carregando, mostra só um "Buscando..." discreto; assim que chega o
// dado, mostra o resumo somente leitura. Ao desmarcar, o item some do
// relatório e os dados importados são descartados. ──
// ── CORREÇÃO: agora mostramos o detalhamento por lançamento (placa/veículo,
// quantidade, valor unitário e total daquele lançamento) sempre que houver
// pelo menos um registro no período — igual ao que aparece no PDF — em vez
// de esconder tudo atrás de uma média. ──
const ItemImportavel = ({ chave, label, tipo, cor, item, onToggle, formatarMoeda }) => {
  const isDesconto = tipo === 'desconto';
  const temItens = Array.isArray(item.itens) && item.itens.length >= 1;
  const temMultiplosValores = Array.isArray(item.itens) && item.itens.length > 1;

  return (
    <div style={{
      ...styles.itemAdicional,
      borderLeft: `4px solid ${cor}`,
      backgroundColor: item.ativo ? (isDesconto ? '#fff5f5' : '#f0fff4') : '#fafafa',
    }}>
      <div style={styles.itemAdicionalHeader} onClick={() => onToggle(chave)}>
        <div style={styles.itemAdicionalCheck}>
          {item.ativo
            ? <FaCheckSquare style={{ color: cor, fontSize: '20px' }} />
            : <FaSquare style={{ color: '#ccc', fontSize: '20px' }} />
          }
          <div>
            <span style={{ ...styles.itemAdicionalLabel, color: item.ativo ? '#333' : '#888' }}>
              {label}
            </span>
            <span style={{
              ...styles.itemAdicionalTipo,
              backgroundColor: isDesconto ? '#fde8e8' : '#e8f5e9',
              color: isDesconto ? '#c0392b' : '#27ae60',
            }}>
              {isDesconto ? '− DESCONTO' : '+ ACRÉSCIMO'}
            </span>
          </div>
        </div>
        {item.ativo && item.importado && item.valorTotal > 0 && (
          <span style={{
            ...styles.itemAdicionalTotalBadge,
            backgroundColor: isDesconto ? '#c0392b' : '#27ae60',
          }}>
            {isDesconto ? '−' : '+'} {formatarMoeda(item.valorTotal)}
          </span>
        )}
      </div>

      {item.ativo && (
        <div style={styles.itemAdicionalCampos}>
          {item.importando ? (
            // ── Buscando os dados — nenhum botão, só um indicador discreto ──
            <div style={styles.importCarregandoBox}>
              <FaSpinner style={styles.spinner} /> Buscando dados do período...
            </div>
          ) : item.importado ? (
            <>
              {/* ── Dados já carregados — resumo somente leitura ── */}
              <div style={styles.importResumoBox} className="rel-item-row">
                <div style={styles.importResumoItem} className="rel-campo-grupo">
                  <span style={styles.campoLabel}>Quantidade</span>
                  <div style={styles.importResumoValor}>{item.quantidade}</div>
                </div>
                {item.valorUnitario > 0 && (
                  <div style={styles.importResumoItem} className="rel-campo-grupo">
                    {/* ── Quando há mais de um valor no período, deixa claro que
                        esse número é uma MÉDIA — o valor real de cada
                        lançamento está no detalhamento logo abaixo, igual
                        ao que aparece no PDF. ── */}
                    <span style={styles.campoLabel}>
                      {temMultiplosValores ? 'Valor Unitário Médio' : 'Valor Unitário'}
                    </span>
                    <div style={styles.importResumoValor}>{formatarMoeda(item.valorUnitario)}</div>
                  </div>
                )}
                <div style={styles.importResumoItem} className="rel-campo-grupo">
                  <span style={styles.campoLabel}>Total</span>
                  <div style={{
                    ...styles.totalCalculado,
                    color: isDesconto ? '#c0392b' : '#27ae60',
                    borderColor: isDesconto ? '#e74c3c' : '#2ecc71',
                  }} className="rel-total-calculado">
                    {isDesconto ? '− ' : '+ '}{formatarMoeda(item.valorTotal)}
                  </div>
                </div>
              </div>

              {/* ── Detalhamento por lançamento — mostra cada linha exatamente
                  como aparece no PDF (placa/veículo/local + quantidade +
                  valor unitário + total daquele lançamento), em vez de
                  esconder tudo atrás de uma média. ── */}
              {temItens && (
                <div style={styles.detalhamentoBox}>
                  <span style={styles.detalhamentoTitle}>
                    Detalhamento ({item.itens.length} lançamento{item.itens.length === 1 ? '' : 's'} no período)
                  </span>
                  <div style={styles.detalhamentoLinhas}>
                    {item.itens.map((sub, idx) => (
                      <div key={idx} style={styles.detalhamentoLinha}>
                        <span style={styles.detalhamentoLinhaEsquerda}>
                          {sub.label ? `${sub.label} — ` : ''}{sub.quantidade}x {formatarMoeda(sub.valorUnitario)}
                        </span>
                        <span style={{
                          ...styles.detalhamentoLinhaValor,
                          color: isDesconto ? '#c0392b' : '#27ae60',
                        }}>
                          {isDesconto ? '− ' : '+ '}{formatarMoeda(sub.valorTotal)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>
      )}
    </div>
  );
};
// ────────────────────────────────────────────────────────────────────────────

function Relatorios() {
  const { API_BASE_URL } = config;
  const { addToast } = useToast();

  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('jwtToken');
    if (token && !isExpired(token)) {
      const decoded = decodeToken(token);
      setUserRole((decoded?.tipoUsuario || '').toUpperCase());
    }
  }, []);

  const isColaborador = userRole === 'COLABORADOR' || userRole === 'FUNCIONARIO';

  const [tipoRelatorio, setTipoRelatorio] = useState('diario');

  useEffect(() => {
    if (isColaborador) {
      setTipoRelatorio('cliente');
    }
  }, [isColaborador]);

  const [dataSelecionada, setDataSelecionada] = useState(new Date().toISOString().split('T')[0]);
  const [dataInicio, setDataInicio] = useState(new Date().toISOString().split('T')[0]);
  const [dataFim, setDataFim] = useState(new Date().toISOString().split('T')[0]);
  const [mesSelecionado, setMesSelecionado] = useState(new Date().toISOString().slice(0, 7));
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [termoPesquisa, setTermoPesquisa] = useState('');
  const [resultadosPesquisa, setResultadosPesquisa] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pesquisandoCliente, setPesquisandoCliente] = useState(false);
  const [modalPesquisaAberto, setModalPesquisaAberto] = useState(false);

  const [modalProdutosAberto, setModalProdutosAberto] = useState(false);
  const [todosProdutos, setTodosProdutos] = useState([]);
  const [produtosSelecionados, setProdutosSelecionados] = useState([]);
  const [termoPesquisaProduto, setTermoPesquisaProduto] = useState('');
  const [carregandoProdutos, setCarregandoProdutos] = useState(false);

  // ── Seleção temporária nos painéis ────────────────────────────────────────
  const [selecaoEsquerda, setSelecaoEsquerda] = useState([]);
  const [selecaoDireita, setSelecaoDireita] = useState([]);

  // ── Estado do modal de itens adicionais ─────────────────────────────────
  const [modalItensAdicionaisAberto, setModalItensAdicionaisAberto] = useState(false);

  // ── Configurações gerais do relatório (persistidas localmente) ───────────
  const [configItensAberta, setConfigItensAberta] = useState(false);
  const [mostrarDescontoCrlv, setMostrarDescontoCrlv] = useState(() => {
    const salvo = localStorage.getItem('relatorio_mostrarDescontoCrlv');
    return salvo === null ? true : salvo === 'true';
  });

  // ── NOVO: estados do modal de EDIÇÃO de cliente, iguais aos usados na
  // busca de cliente da Ordem de Serviço (mesmos campos, mesma máscara,
  // mesma busca de CEP via ViaCEP). ──
  const formClienteVazio = () => ({
    nome: '', identidade: '', cpf: '', cnpj: '', telefone: '', email: '',
    logradouro: '', numero: '', bairro: '', complemento: '', cep: '', cidade: '', uf: ''
  });

  const [modalEdicaoClienteAberto, setModalEdicaoClienteAberto] = useState(false);
  const [clienteEditando, setClienteEditando] = useState(null);
  const [formEdicao, setFormEdicao] = useState(formClienteVazio());
  const [errorsEdicao, setErrorsEdicao] = useState({});
  // Campos alterados pela leitura do print (Ctrl+V), destacados em amarelo até o formulário ser fechado
  const [camposDoPrintEdicao, setCamposDoPrintEdicao] = useState([]);
  // CEP preenchido pelo print: enquanto não mudar, sair do campo CEP não busca o endereço na API
  const [cepDoPrintEdicao, setCepDoPrintEdicao] = useState('');
  const [salvandoEdicao, setSalvandoEdicao] = useState(false);
  const [cpfStatusEdicao, setCpfStatusEdicao] = useState(null);
  const [cnpjStatusEdicao, setCnpjStatusEdicao] = useState(null);
  const [cpfMessageEdicao, setCpfMessageEdicao] = useState('');
  const [cnpjMessageEdicao, setCnpjMessageEdicao] = useState('');
  const [loadingCepEdicao, setLoadingCepEdicao] = useState(false);

  // ── CORREÇÃO: itens adicionais agora carregam quantidade/valor/total
  // IMPORTADOS automaticamente do Painel CRVA assim que o item é marcado —
  // não existe mais digitação manual nem botão de importar. `importado`
  // controla se o dado já chegou; `importando` controla o loading.
  // ── NOVO: `itens` guarda o detalhamento por lançamento (placa/veículo,
  // quantidade, valor unitário, total) — vem direto da lista bruta do
  // endpoint /prerelatorio, igual ao que o PDF usa. ──
  const estadoInicialItens = () => ({
    validacaoProcuracao: { ativo: false, importado: false, importando: false, quantidade: 0, valorUnitario: 0, valorTotal: 0, itens: [] },
    vistoriaForaSede: { ativo: false, importado: false, importando: false, quantidade: 0, valorUnitario: 0, valorTotal: 0, itens: [] },
    descontoCrlv: { ativo: false, importado: false, importando: false, quantidade: 0, valorUnitario: 0, valorTotal: 0, itens: [] },
  });

  const [itensAdicionais, setItensAdicionais] = useState(estadoInicialItens());

  // ── NOVO: sempre que o cliente ou o período mudar, os valores já
  // importados ficam desatualizados — reseta tudo pra evitar mandar pro
  // relatório números de um cliente/período diferente do atual. ──
  useEffect(() => {
    setItensAdicionais(estadoInicialItens());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteSelecionado, dataInicio, dataFim]);

  // ── Quando o item "Desconto CRLV-e" é ocultado nas configurações,
  // garante que ele também fique desmarcado/zerado no estado do relatório,
  // para que nunca entre no cálculo mesmo estando escondido da tela.
  useEffect(() => {
    if (!mostrarDescontoCrlv) {
      setItensAdicionais(prev => ({
        ...prev,
        descontoCrlv: { ativo: false, importado: false, importando: false, quantidade: 0, valorUnitario: 0, valorTotal: 0, itens: [] }
      }));
    }
  }, [mostrarDescontoCrlv]);

  const toggleMostrarDescontoCrlv = () => {
    setMostrarDescontoCrlv(prev => {
      const novo = !prev;
      localStorage.setItem('relatorio_mostrarDescontoCrlv', String(novo));
      return novo;
    });
  };

  const formatarMoeda = (valor) =>
    (valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  // Total "bruto" de cada item (usado para exibir o preview no card, mesmo se inativo)
  const totalValidacao = itensAdicionais.validacaoProcuracao.valorTotal || 0;
  const totalVistoria = itensAdicionais.vistoriaForaSede.valorTotal || 0;
  const totalDesconto = itensAdicionais.descontoCrlv.valorTotal || 0;

  // ── CORREÇÃO: total "efetivo" só considera o valor se o item estiver ATIVO
  // E já tiver sido importado. ──
  const totalValidacaoEfetivo = (itensAdicionais.validacaoProcuracao.ativo && itensAdicionais.validacaoProcuracao.importado) ? totalValidacao : 0;
  const totalVistoriaEfetivo  = (itensAdicionais.vistoriaForaSede.ativo && itensAdicionais.vistoriaForaSede.importado) ? totalVistoria : 0;
  const totalDescontoEfetivo  = (itensAdicionais.descontoCrlv.ativo && itensAdicionais.descontoCrlv.importado) ? totalDesconto : 0;

  const totalLiquidoAdicional = totalValidacaoEfetivo + totalVistoriaEfetivo - totalDescontoEfetivo;

  const getAuthHeader = () => {
    const token = localStorage.getItem('jwtToken');
    return { headers: { 'Authorization': `Bearer ${token}` } };
  };

  // ═══════════════════════════════════════════════════════════════════════
  // NOVO: validações e formatação de documento — mesmas regras usadas na
  // busca/edição de cliente da Ordem de Serviço.
  // ═══════════════════════════════════════════════════════════════════════
  const validarCPF = (cpf) => {
    if (!cpf) return true;
    const c = cpf.replace(/\D/g, '');
    if (c.length !== 11 || /^(\d)\1+$/.test(c)) return false;
    let soma = 0;
    for (let i = 0; i < 9; i++) soma += parseInt(c[i]) * (10 - i);
    let r = 11 - (soma % 11);
    if ((r > 9 ? 0 : r) !== parseInt(c[9])) return false;
    soma = 0;
    for (let i = 0; i < 10; i++) soma += parseInt(c[i]) * (11 - i);
    r = 11 - (soma % 11);
    return (r > 9 ? 0 : r) === parseInt(c[10]);
  };

  const validarCNPJ = (cnpj) => {
    if (!cnpj) return true;
    const c = cnpj.replace(/\D/g, '');
    if (c.length !== 14 || /^(\d)\1+$/.test(c)) return false;
    let tam = c.length - 2, num = c.substring(0, tam), dig = c.substring(tam);
    let soma = 0, pos = tam - 7;
    for (let i = tam; i >= 1; i--) { soma += num.charAt(tam - i) * pos--; if (pos < 2) pos = 9; }
    let res = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    if (res !== parseInt(dig[0])) return false;
    tam++; num = c.substring(0, tam); soma = 0; pos = tam - 7;
    for (let i = tam; i >= 1; i--) { soma += num.charAt(tam - i) * pos--; if (pos < 2) pos = 9; }
    res = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    return res === parseInt(dig[1]);
  };

  const validarEmail = (email) => { if (!email) return true; return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email); };
  const validarCEP = (cep) => { if (!cep) return true; return cep.replace(/\D/g, '').length === 8; };

  const formatarCPF = (cpf) => { if (!cpf) return ''; return cpf.replace(/\D/g, '').replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4'); };
  const formatarCNPJ = (cnpj) => { if (!cnpj) return ''; return cnpj.replace(/\D/g, '').replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5'); };

  // Documento pronto pra exibir na listagem de resultados — cobre tanto
  // clientes com campos cpf/cnpj separados quanto o campo único cpfCnpj.
  const formatarDocumentoCliente = (cliente) => {
    if (cliente.cpf && cliente.cpf.replace(/\D/g, '').length === 11) return `CPF: ${formatarCPF(cliente.cpf)}`;
    if (cliente.cnpj && cliente.cnpj.replace(/\D/g, '').length === 14) return `CNPJ: ${formatarCNPJ(cliente.cnpj)}`;
    if (cliente.cpfCnpj) {
      const n = cliente.cpfCnpj.replace(/\D/g, '');
      if (n.length === 11) return `CPF: ${formatarCPF(n)}`;
      if (n.length === 14) return `CNPJ: ${formatarCNPJ(n)}`;
    }
    return null;
  };

  const getInputBorderStyle = (status, hasError) => {
    if (hasError) return { borderColor: '#e74c3c' };
    if (status === 'checking') return { borderColor: '#f39c12' };
    if (status === 'valid') return { borderColor: '#27ae60' };
    if (status === 'invalid' || status === 'duplicate') return { borderColor: '#e74c3c' };
    return {};
  };

  // ── CORREÇÃO: marcar o item já dispara a importação automaticamente —
  // não existe mais um botão separado de "Importar". Desmarcar remove o
  // item do relatório e descarta os dados carregados.
  //
  // ── CORREÇÃO PRINCIPAL: "Certidão de Validação de Procuração" e "Vistoria
  // Fora da Sede" agora usam os endpoints /prerelatorio/... (lista bruta de
  // lançamentos, os mesmos dados que o backend usa pra montar o PDF) em vez
  // de /importar/..., que só devolvia a média. Assim a tela mostra o mesmo
  // detalhamento (placa/veículo/local) que aparece no PDF, e o total e a
  // quantidade são calculados aqui a partir dos lançamentos reais — sem
  // depender de um cálculo de média feito no backend. "Desconto CRLV-e"
  // continua usando o endpoint de importação existente, sem mudanças. ──
  const toggleItem = async (chave) => {
    const estavaAtivo = itensAdicionais[chave].ativo;

    if (estavaAtivo) {
      // Desmarcando: some do relatório, descarta o que foi importado
      setItensAdicionais(prev => ({
        ...prev,
        [chave]: { ativo: false, importado: false, importando: false, quantidade: 0, valorUnitario: 0, valorTotal: 0, itens: [] },
      }));
      return;
    }

    // Marcando: ativa e já dispara a busca no Painel CRVA, sem intervenção manual
    setItensAdicionais(prev => ({ ...prev, [chave]: { ...prev[chave], ativo: true, importando: true } }));

    if (!clienteSelecionado) return;

    const rotulos = {
      validacaoProcuracao: 'Certidão de Validação de Procuração',
      vistoriaForaSede: 'Vistoria Fora da Sede',
      descontoCrlv: 'Desconto CRLV-e',
    };

    try {
      if (chave === 'validacaoProcuracao') {
        // ── Lista bruta: cada item é uma procuração validada, com sua
        // própria placa e valor. Igual à tabela "PROCURAÇÕES" do PDF. ──
        const response = await axios.get(
          `${API_BASE_URL}/painel-crva/procuracoes/prerelatorio/${clienteSelecionado.idCliente}/${dataInicio}/${dataFim}`,
          getAuthHeader()
        );
        const lista = response.data || [];
        const quantidade = lista.length;
        const valorTotal = lista.reduce((acc, i) => acc + (i.valor || 0), 0);
        const valorUnitario = quantidade > 0 ? valorTotal / quantidade : 0;
        const itens = lista.map(i => ({
          label: i.placa || '—',
          quantidade: 1,
          valorUnitario: i.valor || 0,
          valorTotal: i.valor || 0,
        }));
        setItensAdicionais(prev => ({
          ...prev,
          validacaoProcuracao: { ativo: true, importado: true, importando: false, quantidade, valorUnitario, valorTotal, itens },
        }));
        if (quantidade === 0) addToast('info', `${rotulos[chave]}: nenhum registro encontrado no período`);
        return;
      }

      if (chave === 'vistoriaForaSede') {
        // ── Lista bruta: cada item já vem com sua própria quantidade e
        // valor unitário (ex: leve x pesado, com/sem deslocamento extra).
        // Igual à tabela "VISTORIAS FORA DE SEDE" do PDF. ──
        const response = await axios.get(
          `${API_BASE_URL}/painel-crva/vistoria/prerelatorio/${clienteSelecionado.idCliente}/${dataInicio}/${dataFim}`,
          getAuthHeader()
        );
        const lista = response.data || [];
        const quantidade = lista.reduce((acc, i) => acc + (i.quantidade || 0), 0);
        const valorTotal = lista.reduce((acc, i) => acc + ((i.quantidade || 0) * (i.valorUnitario || 0)), 0);
        const valorUnitario = quantidade > 0 ? valorTotal / quantidade : 0;
        const itens = lista.map(i => ({
          label: [i.veiculo, i.local].filter(Boolean).join(' - ') || '—',
          quantidade: i.quantidade || 0,
          valorUnitario: i.valorUnitario || 0,
          valorTotal: (i.quantidade || 0) * (i.valorUnitario || 0),
        }));
        setItensAdicionais(prev => ({
          ...prev,
          vistoriaForaSede: { ativo: true, importado: true, importando: false, quantidade, valorUnitario, valorTotal, itens },
        }));
        if (quantidade === 0) addToast('info', `${rotulos[chave]}: nenhum registro encontrado no período`);
        return;
      }

      // ── Desconto CRLV-e: sem alteração — continua usando o endpoint de
      // importação já existente, que devolve um único valor agregado. ──
      const response = await axios.get(
        `${API_BASE_URL}/painel-crva/issqn/importar/${clienteSelecionado.idCliente}/${dataInicio}/${dataFim}`,
        getAuthHeader()
      );
      const quantidade = response.data?.quantidade || 0;
      const valorUnitario = response.data?.valorUnitario || 0;
      const valorTotal = response.data?.valorTotal || 0;
      const itens = Array.isArray(response.data?.itens) ? response.data.itens : [];
      setItensAdicionais(prev => ({
        ...prev,
        descontoCrlv: { ativo: true, importado: true, importando: false, quantidade, valorUnitario, valorTotal, itens },
      }));
      if (quantidade === 0) addToast('info', `${rotulos[chave]}: nenhum registro encontrado no período`);
    } catch (error) {
      addToast('error', `Erro ao importar ${rotulos[chave]}: ` + (error.response?.data?.message || error.message));
      // Em caso de erro, desmarca automaticamente — não faz sentido deixar
      // o item marcado sem nenhum dado por trás
      setItensAdicionais(prev => ({
        ...prev,
        [chave]: { ativo: false, importado: false, importando: false, quantidade: 0, valorUnitario: 0, valorTotal: 0, itens: [] },
      }));
    }
  };

  // ── Helpers de seleção nos painéis ────────────────────────────────────────
  const toggleSelecaoEsquerda = (id) => {
    setSelecaoEsquerda(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleSelecaoDireita = (id) => {
    setSelecaoDireita(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const moverParaDireita = () => {
    if (selecaoEsquerda.length === 0) return;
    const itens = produtosDisponiveis.filter(p => selecaoEsquerda.includes(p.idProduto));
    setProdutosSelecionados(prev => [...prev, ...itens]);
    setSelecaoEsquerda([]);
  };

  const moverParaEsquerda = () => {
    if (selecaoDireita.length === 0) return;
    setProdutosSelecionados(prev => prev.filter(p => !selecaoDireita.includes(p.idProduto)));
    setSelecaoDireita([]);
  };

  const moverTodosParaDireita = () => {
    setProdutosSelecionados(prev => [...prev, ...produtosDisponiveis]);
    setSelecaoEsquerda([]);
  };

  const moverTodosParaEsquerda = () => {
    setProdutosSelecionados([]);
    setSelecaoDireita([]);
  };

  // ────────────────────────────────────────────────────────────────────────

  const carregarTodosProdutos = async () => {
    setCarregandoProdutos(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/produtos/ordem`, getAuthHeader());
      setTodosProdutos(response.data.content || response.data || []);
    } catch (error) {
      addToast('error', 'Erro ao carregar produtos');
    } finally {
      setCarregandoProdutos(false);
    }
  };

  const produtosFiltrados = todosProdutos.filter(p =>
    p.nome.toLowerCase().includes(termoPesquisaProduto.toLowerCase())
  );
  const produtosDisponiveis = produtosFiltrados.filter(
    p => !produtosSelecionados.some(s => s.idProduto === p.idProduto)
  );

  const abrirModalProdutos = () => {
    setModalProdutosAberto(true);
    setSelecaoEsquerda([]);
    setSelecaoDireita([]);
    carregarTodosProdutos();
  };

  const formatarCpfCnpj = (valor) => {
    if (!valor) return '';
    const num = valor.replace(/\D/g, '');
    if (num.length === 11) return num.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    if (num.length === 14) return num.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
    return valor;
  };

  // ═══════════════════════════════════════════════════════════════════════
  // NOVO: busca de cliente igual à da Ordem de Serviço — detecta CPF (11
  // dígitos), CNPJ (14 dígitos) ou nome, e usa os mesmos endpoints.
  // ═══════════════════════════════════════════════════════════════════════
  const getBuscaHint = () => {
    const nums = termoPesquisa.replace(/\D/g, '');
    if (nums.length === 11) return { text: 'Buscando por CPF', color: '#27ae60' };
    if (nums.length === 14) return { text: 'Buscando por CNPJ', color: '#3498db' };
    if (termoPesquisa.length >= 2) return { text: 'Buscando por nome', color: '#7f8c8d' };
    return { text: 'Digite nome, CPF ou CNPJ para buscar', color: '#bdc3c7' };
  };

  const pesquisarClientes = async () => {
    if (!termoPesquisa.trim() || termoPesquisa.trim().length < 2) {
      addToast('warning', 'Informe ao menos 2 caracteres para a pesquisa');
      return;
    }
    setPesquisandoCliente(true);
    setResultadosPesquisa([]);
    try {
      const termo = termoPesquisa.trim();
      const apenasNumeros = termo.replace(/\D/g, '');
      let url;
      if (apenasNumeros.length === 11) url = `${API_BASE_URL}/clientes/cpf/${apenasNumeros}`;
      else if (apenasNumeros.length === 14) url = `${API_BASE_URL}/clientes/cnpj/${apenasNumeros}`;
      else url = `${API_BASE_URL}/clientes/nome/${termo}/pagina?page=0&size=20`;

      const response = await axios.get(url, getAuthHeader());
      let lista = [];
      if (Array.isArray(response.data)) lista = response.data;
      else if (response.data?.content) lista = response.data.content;
      else if (response.data?.idCliente) lista = [response.data];

      if (lista.length > 0) {
        setResultadosPesquisa(lista);
      } else {
        addToast('info', 'Nenhum cliente encontrado');
      }
    } catch (error) {
      addToast('error', 'Erro ao pesquisar clientes');
      setResultadosPesquisa([]);
    } finally {
      setPesquisandoCliente(false);
    }
  };

  const selecionarCliente = (cliente) => {
    setClienteSelecionado(cliente);
    setModalPesquisaAberto(false);
    setTermoPesquisa('');
    setResultadosPesquisa([]);
    addToast('success', `Cliente ${cliente.nome} selecionado`);
  };

  // ═══════════════════════════════════════════════════════════════════════
  // NOVO: edição de cliente direto na busca — igual à Ordem de Serviço.
  // Sem CPF/CNPJ, o cliente não pode ser selecionado (a NFS-e nacional
  // exige documento); o botão "Editar" continua liberado, e assim que o
  // CPF/CNPJ for salvo, o botão "Selecionar" habilita sozinho.
  // ═══════════════════════════════════════════════════════════════════════
  const verificarCpfNaApiEdicao = async (cpf) => {
    if (!cpf || cpf.replace(/\D/g, '').length !== 11) return;
    if (!validarCPF(cpf)) { setCpfStatusEdicao('invalid'); setCpfMessageEdicao('CPF inválido'); setErrorsEdicao(prev => ({...prev, cpf: 'CPF inválido'})); return; }
    setCpfStatusEdicao('checking'); setCpfMessageEdicao('Verificando CPF...');
    setErrorsEdicao(prev => { const e = {...prev}; delete e.cpf; return e; });
    try {
      const response = await axios.get(`${API_BASE_URL}/clientes/cpf/${cpf.replace(/\D/g, '')}`, getAuthHeader());
      if (response.data && response.data.idCliente !== clienteEditando?.idCliente) {
        setCpfStatusEdicao('duplicate'); setCpfMessageEdicao('CPF já cadastrado'); setErrorsEdicao(prev => ({...prev, cpf: 'CPF já cadastrado'}));
      } else { setCpfStatusEdicao('valid'); setCpfMessageEdicao('CPF válido'); }
    } catch { setCpfStatusEdicao('valid'); setCpfMessageEdicao('CPF válido'); }
  };

  const verificarCnpjNaApiEdicao = async (cnpj) => {
    if (!cnpj || cnpj.replace(/\D/g, '').length !== 14) return;
    if (!validarCNPJ(cnpj)) { setCnpjStatusEdicao('invalid'); setCnpjMessageEdicao('CNPJ inválido'); setErrorsEdicao(prev => ({...prev, cnpj: 'CNPJ inválido'})); return; }
    setCnpjStatusEdicao('checking'); setCnpjMessageEdicao('Verificando CNPJ...');
    setErrorsEdicao(prev => { const e = {...prev}; delete e.cnpj; return e; });
    try {
      const response = await axios.get(`${API_BASE_URL}/clientes/cnpj/${cnpj.replace(/\D/g, '')}`, getAuthHeader());
      if (response.data && response.data.idCliente !== clienteEditando?.idCliente) {
        setCnpjStatusEdicao('duplicate'); setCnpjMessageEdicao('CNPJ já cadastrado'); setErrorsEdicao(prev => ({...prev, cnpj: 'CNPJ já cadastrado'}));
      } else { setCnpjStatusEdicao('valid'); setCnpjMessageEdicao('CNPJ válido'); }
    } catch { setCnpjStatusEdicao('valid'); setCnpjMessageEdicao('CNPJ válido'); }
  };

  const handleCpfChangeEdicao = (e) => {
    const value = e.target.value;
    setFormEdicao(prev => ({...prev, cpf: value}));
    const limpo = value.replace(/\D/g, '');
    if (limpo.length === 0) { setCpfStatusEdicao(null); setCpfMessageEdicao(''); return; }
    if (limpo.length === 11) setTimeout(() => verificarCpfNaApiEdicao(value), 800);
    else { setCpfStatusEdicao(null); setCpfMessageEdicao(''); }
  };

  const handleCnpjChangeEdicao = (e) => {
    const value = e.target.value;
    setFormEdicao(prev => ({...prev, cnpj: value}));
    const limpo = value.replace(/\D/g, '');
    if (limpo.length === 0) { setCnpjStatusEdicao(null); setCnpjMessageEdicao(''); return; }
    if (limpo.length === 14) setTimeout(() => verificarCnpjNaApiEdicao(value), 800);
    else { setCnpjStatusEdicao(null); setCnpjMessageEdicao(''); }
  };

  // Lupa do CNPJ na edição do cliente: completa só os campos em branco (cadastro antigo/incompleto),
  // sem trocar o que já está preenchido
  const [buscandoCnpjEdicao, setBuscandoCnpjEdicao] = useState(false);
  const podeConsultarCnpjEdicao = (formEdicao.cnpj || '').replace(/\D/g, '').length === 14
    && cnpjStatusEdicao === 'valid'
    && !salvandoEdicao
    && !buscandoCnpjEdicao;

  const preencherPorCnpjEdicao = async () => {
    if (!podeConsultarCnpjEdicao) return;
    setBuscandoCnpjEdicao(true);
    const { dados, erro } = await consultarCnpj(formEdicao.cnpj, API_BASE_URL, getAuthHeader());
    setBuscandoCnpjEdicao(false);
    if (!dados) { addToast('error', erro); return; }
    const { novo, completados } = completarComDadosCnpj(formEdicao, dados);
    setFormEdicao(novo);
    const aviso = avisoCompletarCnpj(completados);
    addToast(aviso.tipo, aviso.texto);
  };

  const buscarEnderecoPorCEPEdicao = async (cep) => {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length !== 8) return;
    setLoadingCepEdicao(true);
    try {
      const response = await axios.get(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      if (response.data && !response.data.erro) {
        setFormEdicao(prev => ({ ...prev, logradouro: response.data.logradouro || prev.logradouro, bairro: response.data.bairro || prev.bairro, cidade: response.data.localidade || prev.cidade, uf: response.data.uf || prev.uf, complemento: response.data.complemento || prev.complemento }));
        addToast('success', 'Endereço encontrado!');
      } else addToast('warning', 'CEP não encontrado');
    } catch { addToast('error', 'Erro ao buscar CEP'); }
    finally { setLoadingCepEdicao(false); }
  };

  const validarFormularioEdicao = () => {
    const newErrors = {};
    if (!formEdicao.nome || formEdicao.nome.trim().length < 3) newErrors.nome = 'Nome deve ter pelo menos 3 caracteres';
    if (formEdicao.email && !validarEmail(formEdicao.email)) newErrors.email = 'Email inválido';
    if (formEdicao.cpf && formEdicao.cpf.replace(/\D/g, '').length > 0) {
      if (!validarCPF(formEdicao.cpf)) newErrors.cpf = 'CPF inválido';
      else if (cpfStatusEdicao === 'duplicate') newErrors.cpf = 'CPF já cadastrado';
    }
    if (formEdicao.cnpj && formEdicao.cnpj.replace(/\D/g, '').length > 0) {
      if (!validarCNPJ(formEdicao.cnpj)) newErrors.cnpj = 'CNPJ inválido';
      else if (cnpjStatusEdicao === 'duplicate') newErrors.cnpj = 'CNPJ já cadastrado';
    }
    if (formEdicao.cep && !validarCEP(formEdicao.cep)) newErrors.cep = 'CEP inválido';
    setErrorsEdicao(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetFormEdicao = () => {
    setFormEdicao(formClienteVazio());
    setCamposDoPrintEdicao([]);
    setCepDoPrintEdicao('');
    setErrorsEdicao({});
    setCpfStatusEdicao(null);
    setCnpjStatusEdicao(null);
    setCpfMessageEdicao('');
    setCnpjMessageEdicao('');
    setClienteEditando(null);
  };

  const abrirModalEdicaoCliente = async (cliente) => {
    setPesquisandoCliente(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/clientes/${cliente.idCliente}`, getAuthHeader());
      const dados = response.data;
      setClienteEditando(dados);
      setFormEdicao({
        nome: dados.nome || '',
        identidade: dados.identidade || '',
        cpf: dados.cpf || '',
        cnpj: dados.cnpj || '',
        telefone: dados.telefone || '',
        email: dados.email || '',
        logradouro: dados.logradouro || '',
        numero: dados.numero || '',
        bairro: dados.bairro || '',
        complemento: dados.complemento || '',
        cep: dados.cep || '',
        cidade: dados.cidade || '',
        uf: dados.uf || '',
      });
      setErrorsEdicao({});
      if (dados.cpf) { setCpfStatusEdicao('valid'); setCpfMessageEdicao('CPF já cadastrado'); } else { setCpfStatusEdicao(null); setCpfMessageEdicao(''); }
      if (dados.cnpj) { setCnpjStatusEdicao('valid'); setCnpjMessageEdicao('CNPJ já cadastrado'); } else { setCnpjStatusEdicao(null); setCnpjMessageEdicao(''); }
      setModalEdicaoClienteAberto(true);
    } catch {
      addToast('error', 'Erro ao carregar dados do cliente');
    } finally {
      setPesquisandoCliente(false);
    }
  };

  // Print colado (Ctrl+V) com os dados do cliente: sobrescreve os campos lidos e destaca os que mudaram. Nada é salvo sozinho.
  const aplicarPrintEdicao = (dados, alterados) => {
    setFormEdicao(prev => ({ ...prev, ...dados }));
    setCamposDoPrintEdicao(prev => [...new Set([...prev, ...alterados])]);
    if (dados.cep) setCepDoPrintEdicao(dados.cep);
    setErrorsEdicao(prev => { const e = { ...prev }; Object.keys(dados).forEach(campo => delete e[campo]); return e; });
    if (dados.cpf) verificarCpfNaApiEdicao(dados.cpf);
    if (dados.cnpj) verificarCnpjNaApiEdicao(dados.cnpj);
  };

  const destaquePrintEdicao = (campo) => (camposDoPrintEdicao.includes(campo) ? ESTILO_CAMPO_DO_PRINT : {});

  const fecharModalEdicaoCliente = () => { setModalEdicaoClienteAberto(false); resetFormEdicao(); };

  const salvarEdicaoCliente = async () => {
    if (!validarFormularioEdicao()) { addToast('warning', 'Corrija os erros antes de salvar'); return; }
    setSalvandoEdicao(true);
    try {
      const clienteData = { nome: formEdicao.nome, identidade: formEdicao.identidade || null, cpf: formEdicao.cpf || null, cnpj: formEdicao.cnpj || null, telefone: formEdicao.telefone || null, email: formEdicao.email || null, logradouro: formEdicao.logradouro || null, numero: formEdicao.numero || null, bairro: formEdicao.bairro || null, complemento: formEdicao.complemento || null, cep: formEdicao.cep || null, cidade: formEdicao.cidade || null, uf: formEdicao.uf || null };
      const response = await axios.put(`${API_BASE_URL}/clientes/${clienteEditando.idCliente}`, clienteData, getAuthHeader());
      const clienteAtualizado = response.data && Object.keys(response.data).length > 0 ? response.data : { ...clienteEditando, ...clienteData };

      addToast('success', 'Cliente atualizado com sucesso!');

      setResultadosPesquisa(prev => prev.map(c => c.idCliente === clienteAtualizado.idCliente ? { ...c, ...clienteAtualizado } : c));

      if (clienteSelecionado?.idCliente === clienteAtualizado.idCliente) setClienteSelecionado(clienteAtualizado);

      fecharModalEdicaoCliente();
    } catch (err) {
      addToast('error', 'Erro ao atualizar cliente: ' + (err.response?.data?.message || err.message));
    } finally {
      setSalvandoEdicao(false);
    }
  };

  const gerarRelatorioDiario = async () => {
    if (!dataSelecionada) { addToast('warning', 'Selecione uma data'); return; }
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/financeiro/relatorio/${dataSelecionada}`,
        { ...getAuthHeader(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio_caixa_${dataSelecionada}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      addToast('success', 'Relatório gerado com sucesso!');
    } catch (error) {
      addToast('error', 'Erro ao gerar relatório. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // ── CORREÇÃO: além de mandar quantidade/valorUnitario (médio, mantido por
  // compatibilidade), agora também manda o detalhamento por lançamento
  // (`...Itens`) sempre que houver pelo menos um lançamento no período — o
  // backend usa isso pra imprimir cada valor separado no PDF, em vez de uma
  // única linha com a média (ex: "(8) X 9,38"). ──
  const gerarRelatorioCliente = async (itens) => {
    if (!clienteSelecionado) { addToast('warning', 'Selecione um cliente'); return; }
    if (!dataInicio || !dataFim) { addToast('warning', 'Selecione o período'); return; }

    // ── CORREÇÃO: impede gerar o relatório enquanto algum item marcado ainda
    // está sendo importado ou falhou em importar — evita mandar dado zerado ──
    const pendente = Object.entries(itens).find(([, item]) => item.ativo && (item.importando || !item.importado));
    if (pendente) {
      addToast('warning', 'Aguarde a importação dos itens marcados antes de gerar o relatório');
      return;
    }

    setLoading(true);
    setModalItensAdicionaisAberto(false);
    try {
      const params = new URLSearchParams();

      if (itens.validacaoProcuracao.ativo && itens.validacaoProcuracao.importado && itens.validacaoProcuracao.quantidade > 0) {
        params.append('validacaoProcuracaoQtd', itens.validacaoProcuracao.quantidade);
        params.append('validacaoProcuracaoValor', itens.validacaoProcuracao.valorUnitario);
        if (Array.isArray(itens.validacaoProcuracao.itens) && itens.validacaoProcuracao.itens.length > 1) {
          params.append('validacaoProcuracaoItens', JSON.stringify(
            itens.validacaoProcuracao.itens.map(i => ({ quantidade: i.quantidade, valorUnitario: i.valorUnitario }))
          ));
        }
      }
      if (itens.vistoriaForaSede.ativo && itens.vistoriaForaSede.importado && itens.vistoriaForaSede.quantidade > 0) {
        params.append('vistoriaForaSedeQtd', itens.vistoriaForaSede.quantidade);
        params.append('vistoriaForaSedeValor', itens.vistoriaForaSede.valorUnitario);
        if (Array.isArray(itens.vistoriaForaSede.itens) && itens.vistoriaForaSede.itens.length > 1) {
          params.append('vistoriaForaSedeItens', JSON.stringify(
            itens.vistoriaForaSede.itens.map(i => ({ quantidade: i.quantidade, valorUnitario: i.valorUnitario }))
          ));
        }
      }
      if (itens.descontoCrlv.ativo && itens.descontoCrlv.importado && itens.descontoCrlv.quantidade > 0) {
        params.append('descontoCrlvQtd', itens.descontoCrlv.quantidade);
        params.append('descontoCrlvValor', itens.descontoCrlv.valorUnitario);
        if (Array.isArray(itens.descontoCrlv.itens) && itens.descontoCrlv.itens.length > 1) {
          params.append('descontoCrlvItens', JSON.stringify(
            itens.descontoCrlv.itens.map(i => ({ quantidade: i.quantidade, valorUnitario: i.valorUnitario }))
          ));
        }
      }

      const queryString = params.toString() ? `?${params.toString()}` : '';

      const response = await axios.get(
        `${API_BASE_URL}/ordemservico/relatorio/cliente/${clienteSelecionado.idCliente}/${dataInicio}/${dataFim}${queryString}`,
        { ...getAuthHeader(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio_cliente_${clienteSelecionado.nome.replace(/\s/g, '_')}_${dataInicio}_a_${dataFim}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      addToast('success', 'Relatório gerado com sucesso!');
    } catch (error) {
      addToast('error', 'Erro ao gerar relatório. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const gerarRelatorioMensal = async () => {
    if (!mesSelecionado) { addToast('warning', 'Selecione um mês'); return; }
    if (produtosSelecionados.length === 0) { addToast('warning', 'Selecione pelo menos um produto'); return; }
    setLoading(true);
    setModalProdutosAberto(false);
    try {
      const [ano, mes] = mesSelecionado.split('-');
      const idsProdutos = produtosSelecionados.map(p => p.idProduto).join(',');
      const response = await axios.get(
        `${API_BASE_URL}/ordemservico/relatorio/mensal/${ano}/${mes}?produtos=${idsProdutos}`,
        { ...getAuthHeader(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio_mensal_${mesSelecionado}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      addToast('success', 'Relatório mensal gerado com sucesso!');
    } catch (error) {
      addToast('error', 'Erro ao gerar relatório mensal. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleGerarRelatorio = () => {
    if (tipoRelatorio === 'diario') gerarRelatorioDiario();
    else if (tipoRelatorio === 'cliente') {
      if (!clienteSelecionado) { addToast('warning', 'Selecione um cliente'); return; }
      if (!dataInicio || !dataFim) { addToast('warning', 'Selecione o período'); return; }
      setModalItensAdicionaisAberto(true);
    }
    else abrirModalProdutos();
  };

  // ── Mês formatado para exibição no modal ──────────────────────────────────
  const nomeMes = mesSelecionado
    ? new Date(`${mesSelecionado}-02`).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
    : '';

  return (
    <div style={styles.container}>
      <Header />
      <div style={styles.content}>
        <div style={styles.header}>
          <div style={styles.titleContainer}>
            <FaFileAlt style={styles.titleIcon} />
            <h1 style={styles.title}>Relatórios Financeiros</h1>
          </div>
        </div>

        {/* ── Seleção de tipo ──────────────────────────────────────────────── */}
        <div style={{
          ...styles.tipoContainer,
          gridTemplateColumns: isColaborador ? '1fr' : 'repeat(3, 1fr)'
        }}>
          {!isColaborador && (
            <div
              style={{ ...styles.tipoOption, ...(tipoRelatorio === 'diario' && styles.tipoOptionActive) }}
              onClick={() => setTipoRelatorio('diario')}
            >
              <FaCalendarAlt style={styles.tipoIcon} />
              <div>
                <h3 style={styles.tipoTitle}>Relatório Diário</h3>
                <p style={styles.tipoDescription}>Relatório completo do caixa do dia</p>
              </div>
            </div>
          )}

          <div
            style={{ ...styles.tipoOption, ...(tipoRelatorio === 'cliente' && styles.tipoOptionActive) }}
            onClick={() => setTipoRelatorio('cliente')}
          >
            <FaUser style={styles.tipoIcon} />
            <div>
              <h3 style={styles.tipoTitle}>Relatório por Cliente</h3>
              <p style={styles.tipoDescription}>Relatório de serviços por período para um cliente</p>
            </div>
          </div>

          {!isColaborador && (
            <div
              style={{ ...styles.tipoOption, ...(tipoRelatorio === 'mensal' && styles.tipoOptionActive) }}
              onClick={() => setTipoRelatorio('mensal')}
            >
              <FaChartBar style={styles.tipoIcon} />
              <div>
                <h3 style={styles.tipoTitle}>Relatório Mensal</h3>
                <p style={styles.tipoDescription}>Relatório mensal das ordens de serviço</p>
              </div>
            </div>
          )}
        </div>

        {/* ── Formulário Diário ─────────────────────────────────────────────── */}
        {tipoRelatorio === 'diario' && !isColaborador && (
          <div style={styles.formContainer}>
            <div style={styles.formHeader}>
              <FaCalendarAlt style={styles.formIcon} />
              <h2 style={styles.formTitle}>Relatório Diário de Caixa</h2>
            </div>
            <div style={styles.formRow}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Data</label>
                <input type="date" value={dataSelecionada}
                  onChange={(e) => setDataSelecionada(e.target.value)}
                  style={styles.input} max={new Date().toISOString().split('T')[0]} />
                <span style={styles.hintText}>Selecione o dia para gerar o relatório</span>
              </div>
            </div>
            <div style={styles.infoBox}>
              <FaBuilding style={styles.infoIcon} />
              <div>
                <strong>Informações do relatório:</strong>
                <ul style={styles.infoList}>
                  <li>Ordens de serviço do período (agrupadas por cliente)</li>
                  <li>Depósitos realizados no período</li>
                  <li>Saques realizados no período</li>
                  <li>Resumo financeiro (saldo inicial, entradas, saídas, total)</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* ── Formulário por Cliente ────────────────────────────────────────── */}
        {tipoRelatorio === 'cliente' && (
          <div style={styles.formContainer}>
            <div style={styles.formHeader}>
              <FaUser style={styles.formIcon} />
              <h2 style={styles.formTitle}>Relatório por Cliente</h2>
            </div>
            <div style={styles.formRow}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Cliente *</label>
                {clienteSelecionado ? (
                  <div style={styles.clienteSelecionado}>
                    <div style={styles.clienteInfo}>
                      <strong>{clienteSelecionado.nome}</strong>
                      <span style={styles.clienteDocumento}>{formatarDocumentoCliente(clienteSelecionado) || formatarCpfCnpj(clienteSelecionado.cpfCnpj)}</span>
                    </div>
                    <button onClick={() => setClienteSelecionado(null)} style={styles.removerClienteButton}>
                      Trocar Cliente
                    </button>
                  </div>
                ) : (
                  <div>
                    <button onClick={() => setModalPesquisaAberto(true)} style={styles.selecionarClienteButton}>
                      <FaUser /> Selecionar Cliente
                    </button>
                    <span style={styles.hintText}>Selecione um cliente para gerar o relatório</span>
                  </div>
                )}
              </div>
            </div>
            <div style={styles.formRow}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Data Início *</label>
                <input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)}
                  style={styles.input} max={new Date().toISOString().split('T')[0]} />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Data Fim *</label>
                <input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)}
                  style={styles.input} min={dataInicio} max={new Date().toISOString().split('T')[0]} />
              </div>
            </div>
            <div style={styles.infoBox}>
              <FaChartLine style={styles.infoIcon} />
              <div>
                <strong>Informações do relatório:</strong>
                <ul style={styles.infoList}>
                  <li>Todas as ordens de serviço do cliente no período</li>
                  <li>Detalhamento por OS com serviços, placas e valores</li>
                  <li>Total geral dos serviços</li>
                  <li>Espaço para assinatura do cliente e empresa</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* ── Formulário Mensal ─────────────────────────────────────────────── */}
        {tipoRelatorio === 'mensal' && !isColaborador && (
          <div style={styles.formContainer}>
            <div style={styles.formHeader}>
              <FaChartBar style={styles.formIcon} />
              <h2 style={styles.formTitle}>Relatório Mensal das Ordens de Serviço</h2>
            </div>
            <div style={styles.formRow}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Mês/Ano *</label>
                <input type="month" value={mesSelecionado} onChange={(e) => setMesSelecionado(e.target.value)}
                  style={styles.input} max={new Date().toISOString().slice(0, 7)} />
                <span style={styles.hintText}>Selecione o mês para gerar o relatório</span>
              </div>
            </div>
            <div style={styles.infoBox}>
              <FaBoxes style={styles.infoIcon} />
              <div>
                <strong>Produtos Selecionados:</strong>
                {produtosSelecionados.length > 0 ? (
                  <div style={styles.resumoProdutosSelecionados}>
                    {produtosSelecionados.map(p => (
                      <span key={p.idProduto} style={styles.produtoResumo}>{p.nome}</span>
                    ))}
                  </div>
                ) : (
                  <p style={styles.semProdutosMsg}>Nenhum produto selecionado. Clique em "Gerar Relatório" para selecionar os produtos.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Botão Gerar ──────────────────────────────────────────────────── */}
        <button onClick={handleGerarRelatorio} style={styles.gerarButton} disabled={loading}>
          {loading
            ? <><FaSpinner style={styles.spinner} /> Gerando Relatório...</>
            : <><FaDownload /> Gerar Relatório</>
          }
        </button>

        {/* ══════════════════════════════════════════════════════════════════════
            MODAL DE PRODUTOS — RELATÓRIO MENSAL (REESTILIZADO)
        ══════════════════════════════════════════════════════════════════════ */}
        {modalProdutosAberto && (
          <div style={styles.modalOverlay} className="rel-overlay">
            <div style={styles.modalMensal} className="rel-modal-mensal">

              {/* Cabeçalho */}
              <div style={styles.modalMensalHeader} className="rel-modal-mensal-header">
                <div style={styles.modalMensalHeaderLeft}>
                  <FaChartBar style={{ color: '#2ecc71', fontSize: '20px' }} />
                  <div>
                    <h2 style={styles.modalMensalTitle}>Relatório Mensal das Ordens de Serviço</h2>
                    <p style={styles.modalMensalSubtitle}>
                      Selecione os produtos que irão compor o relatório de <strong style={{ textTransform: 'capitalize' }}>{nomeMes}</strong>
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => { setModalProdutosAberto(false); setTermoPesquisaProduto(''); setSelecaoEsquerda([]); setSelecaoDireita([]); }}
                  style={styles.modalMensalClose}
                >
                  <FaTimes />
                </button>
              </div>

              {/* Corpo: dois painéis + controles centrais */}
              <div style={styles.modalMensalBody} className="rel-modal-mensal-body">

                {/* ── Painel esquerdo: Produtos disponíveis ── */}
                <div style={styles.painel} className="rel-painel">
                  <div style={styles.painelHeader}>
                    <span style={styles.painelTitle}>Produtos disponíveis</span>
                    <span style={styles.painelContador}>{produtosDisponiveis.length}</span>
                  </div>

                  {/* Busca */}
                  <div style={styles.painelBusca}>
                    <FaSearch style={styles.painelBuscaIcon} />
                    <input
                      type="text"
                      placeholder="Filtrar produtos..."
                      value={termoPesquisaProduto}
                      onChange={(e) => { setTermoPesquisaProduto(e.target.value); setSelecaoEsquerda([]); }}
                      style={styles.painelBuscaInput}
                    />
                    {termoPesquisaProduto && (
                      <button onClick={() => setTermoPesquisaProduto('')} style={styles.painelBuscaClear}>
                        <FaTimes size={11} />
                      </button>
                    )}
                  </div>

                  {/* Lista */}
                  <div style={styles.painelLista} className="rel-painel-lista">
                    {carregandoProdutos ? (
                      <div style={styles.painelVazio}>
                        <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: '24px', color: '#ccc' }} />
                        <span>Carregando produtos...</span>
                      </div>
                    ) : produtosDisponiveis.length === 0 ? (
                      <div style={styles.painelVazio}>
                        <FaBoxes style={{ fontSize: '32px', color: '#ddd' }} />
                        <span style={{ fontSize: '13px', color: '#aaa' }}>
                          {termoPesquisaProduto ? 'Nenhum resultado' : 'Todos os produtos foram adicionados'}
                        </span>
                      </div>
                    ) : (
                      produtosDisponiveis.map(p => {
                        const selecionado = selecaoEsquerda.includes(p.idProduto);
                        return (
                          <div
                            key={p.idProduto}
                            style={{
                              ...styles.painelItem,
                              backgroundColor: selecionado ? '#e8f5e9' : 'white',
                              borderColor: selecionado ? '#2ecc71' : '#e8e8e8',
                              fontWeight: selecionado ? '600' : '400',
                            }}
                            onClick={() => toggleSelecaoEsquerda(p.idProduto)}
                            onDoubleClick={() => {
                              setProdutosSelecionados(prev => [...prev, p]);
                              setSelecaoEsquerda(prev => prev.filter(x => x !== p.idProduto));
                            }}
                            title="Clique para selecionar • Duplo clique para mover"
                          >
                            <span style={styles.painelItemNome}>{p.nome}</span>
                            {selecionado && <span style={styles.painelItemCheck}>✓</span>}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* ── Controles centrais ── */}
                <div style={styles.controlesCentro} className="rel-controles-centro">
                  <button
                    style={{
                      ...styles.ctrlBtn,
                      ...(selecaoEsquerda.length === 0 ? styles.ctrlBtnDisabled : styles.ctrlBtnActive),
                    }}
                    onClick={moverParaDireita}
                    disabled={selecaoEsquerda.length === 0}
                    title="Mover selecionados →"
                  >
                    <FaArrowRight size={13} />
                  </button>

                  <button
                    style={{
                      ...styles.ctrlBtn,
                      ...(selecaoDireita.length === 0 ? styles.ctrlBtnDisabled : styles.ctrlBtnDanger),
                    }}
                    onClick={moverParaEsquerda}
                    disabled={selecaoDireita.length === 0}
                    title="← Remover selecionados"
                  >
                    <FaArrowLeft size={13} />
                  </button>

                  <div style={styles.ctrlDivisor} />

                  <button
                    style={{
                      ...styles.ctrlBtn,
                      ...(produtosDisponiveis.length === 0 ? styles.ctrlBtnDisabled : styles.ctrlBtnActive),
                      fontSize: '10px',
                      padding: '8px 10px',
                      flexDirection: 'column',
                      gap: '2px',
                    }}
                    onClick={moverTodosParaDireita}
                    disabled={produtosDisponiveis.length === 0}
                    title="Mover todos →"
                  >
                    <FaArrowRight size={11} />
                    <span style={{ fontSize: '9px', letterSpacing: '0.3px' }}>TODOS</span>
                  </button>

                  <button
                    style={{
                      ...styles.ctrlBtn,
                      ...(produtosSelecionados.length === 0 ? styles.ctrlBtnDisabled : styles.ctrlBtnDanger),
                      fontSize: '10px',
                      padding: '8px 10px',
                      flexDirection: 'column',
                      gap: '2px',
                    }}
                    onClick={moverTodosParaEsquerda}
                    disabled={produtosSelecionados.length === 0}
                    title="← Remover todos"
                  >
                    <FaArrowLeft size={11} />
                    <span style={{ fontSize: '9px', letterSpacing: '0.3px' }}>TODOS</span>
                  </button>
                </div>

                {/* ── Painel direito: Produtos no relatório ── */}
                <div style={styles.painel} className="rel-painel">
                  <div style={{ ...styles.painelHeader, backgroundColor: '#f0fff4', borderColor: '#c8e6c9' }}>
                    <span style={{ ...styles.painelTitle, color: '#2e7d32' }}>Produtos no relatório</span>
                    <span style={{
                      ...styles.painelContador,
                      backgroundColor: produtosSelecionados.length > 0 ? '#2ecc71' : '#ccc',
                    }}>
                      {produtosSelecionados.length}
                    </span>
                  </div>

                  <div style={styles.painelLista} className="rel-painel-lista">
                    {produtosSelecionados.length === 0 ? (
                      <div style={styles.painelVazio}>
                        <FaArrowRight style={{ fontSize: '28px', color: '#ddd' }} />
                        <span style={{ fontSize: '13px', color: '#aaa', textAlign: 'center' }}>
                          Mova produtos da lista<br />ao lado para incluir no relatório
                        </span>
                      </div>
                    ) : (
                      produtosSelecionados.map(p => {
                        const selecionado = selecaoDireita.includes(p.idProduto);
                        return (
                          <div
                            key={p.idProduto}
                            style={{
                              ...styles.painelItem,
                              backgroundColor: selecionado ? '#fde8e8' : '#f9fff9',
                              borderColor: selecionado ? '#e74c3c' : '#c8e6c9',
                            }}
                            onClick={() => toggleSelecaoDireita(p.idProduto)}
                            onDoubleClick={() => {
                              setProdutosSelecionados(prev => prev.filter(x => x.idProduto !== p.idProduto));
                              setSelecaoDireita(prev => prev.filter(x => x !== p.idProduto));
                            }}
                            title="Clique para selecionar • Duplo clique para remover"
                          >
                            <span style={styles.painelItemNome}>{p.nome}</span>
                            {selecionado
                              ? <FaTimes size={12} style={{ color: '#e74c3c', flexShrink: 0 }} />
                              : <span style={{ ...styles.painelItemCheck, backgroundColor: '#2ecc71' }}>✓</span>
                            }
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>

              {/* Dica de uso */}
              <div style={styles.modalMensalDica}>
                💡 Clique para selecionar um item • Duplo clique para mover diretamente • Use os botões centrais para mover em grupo
              </div>

              {/* Rodapé */}
              <div style={styles.modalMensalFooter}>
                <button
                  onClick={() => { setModalProdutosAberto(false); setTermoPesquisaProduto(''); setSelecaoEsquerda([]); setSelecaoDireita([]); }}
                  style={styles.secondaryButton}
                >
                  Cancelar
                </button>
                <button
                  onClick={gerarRelatorioMensal}
                  style={{
                    ...styles.primaryButton,
                    opacity: produtosSelecionados.length === 0 ? 0.5 : 1,
                    cursor: produtosSelecionados.length === 0 ? 'not-allowed' : 'pointer',
                  }}
                  disabled={produtosSelecionados.length === 0 || loading}
                >
                  {loading
                    ? <><FaSpinner style={styles.spinner} /> Gerando...</>
                    : <><FaDownload /> Gerar Relatório ({produtosSelecionados.length} produto{produtosSelecionados.length !== 1 ? 's' : ''})</>
                  }
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Modal de Itens Adicionais ─────────────────────────────────────── */}
        {modalItensAdicionaisAberto && (
          <div style={styles.modalOverlay} className="rel-overlay">
            <div style={styles.modalItens} className="rel-modal-itens">
              <div style={styles.modalHeader}>
                <div>
                  <h2 style={styles.modalTitle}>
                    <FaFileAlt style={{ marginRight: '8px', color: '#2ecc71' }} />
                    Itens Adicionais ao Relatório
                  </h2>
                  <p style={styles.modalSubtitle}>
                    Cliente: <strong>{clienteSelecionado?.nome}</strong> &nbsp;|&nbsp;
                    Período: <strong>{dataInicio}</strong> até <strong>{dataFim}</strong>
                  </p>
                </div>
                <div style={styles.modalHeaderActions}>
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={() => setConfigItensAberta(prev => !prev)}
                      style={{
                        ...styles.modalConfigBtn,
                        ...(configItensAberta ? styles.modalConfigBtnAtivo : {}),
                      }}
                      title="Configurações do relatório"
                    >
                      <FaCog />
                    </button>
                    {configItensAberta && (
                      <div style={styles.configPainel}>
                        <div style={styles.configPainelTitle}>Configurações</div>
                        <label style={styles.configPainelItem}>
                          <input
                            type="checkbox"
                            checked={mostrarDescontoCrlv}
                            onChange={toggleMostrarDescontoCrlv}
                            style={styles.configPainelCheckbox}
                          />
                          Exibir opção "Desconto CRLV-e"
                        </label>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => { setModalItensAdicionaisAberto(false); setConfigItensAberta(false); }}
                    style={styles.modalClose}
                  >
                    &times;
                  </button>
                </div>
              </div>

              <div style={styles.modalBodyItens}>
                <p style={styles.instrucaoTexto}>
                  Marque os itens que deseja incluir no relatório — os valores são buscados automaticamente no
                  Painel CRVA para este cliente e período. Os acréscimos serão somados e os descontos
                  subtraídos do valor total dos recibos.
                </p>

                <ItemImportavel
                  chave="validacaoProcuracao"
                  label="Certidão de Validação de Procuração"
                  tipo="acrescimo"
                  cor="#3498db"
                  item={itensAdicionais.validacaoProcuracao}
                  onToggle={toggleItem}
                  formatarMoeda={formatarMoeda}
                />

                <ItemImportavel
                  chave="vistoriaForaSede"
                  label="Vistoria Fora da Sede"
                  tipo="acrescimo"
                  cor="#9b59b6"
                  item={itensAdicionais.vistoriaForaSede}
                  onToggle={toggleItem}
                  formatarMoeda={formatarMoeda}
                />

                {mostrarDescontoCrlv && (
                  <ItemImportavel
                    chave="descontoCrlv"
                    label="Desconto CRLV-e"
                    tipo="desconto"
                    cor="#e74c3c"
                    item={itensAdicionais.descontoCrlv}
                    onToggle={toggleItem}
                    formatarMoeda={formatarMoeda}
                  />
                )}

                {(itensAdicionais.validacaoProcuracao.ativo || itensAdicionais.vistoriaForaSede.ativo || (mostrarDescontoCrlv && itensAdicionais.descontoCrlv.ativo)) && (
                  <div style={styles.resumoAjuste}>
                    <div style={styles.resumoAjusteTitle}>Incrementos ao Relatório</div>
                    <div style={styles.resumoAjusteLinhas}>
                      {itensAdicionais.validacaoProcuracao.ativo && itensAdicionais.validacaoProcuracao.importado && totalValidacao > 0 && (
                        <div style={styles.resumoLinha}>
                          <span>Certidão de Validação de Procuração</span>
                          <span style={{ color: '#27ae60' }}>+ {formatarMoeda(totalValidacao)}</span>
                        </div>
                      )}
                      {itensAdicionais.vistoriaForaSede.ativo && itensAdicionais.vistoriaForaSede.importado && totalVistoria > 0 && (
                        <div style={styles.resumoLinha}>
                          <span>Vistoria Fora da Sede</span>
                          <span style={{ color: '#27ae60' }}>+ {formatarMoeda(totalVistoria)}</span>
                        </div>
                      )}
                      {mostrarDescontoCrlv && itensAdicionais.descontoCrlv.ativo && itensAdicionais.descontoCrlv.importado && totalDesconto > 0 && (
                        <div style={styles.resumoLinha}>
                          <span>Desconto CRLV-e</span>
                          <span style={{ color: '#c0392b' }}>− {formatarMoeda(totalDesconto)}</span>
                        </div>
                      )}
                      <div style={styles.resumoLinhaTotalAjuste}>
                        <span>Ajuste líquido sobre o total dos recibos</span>
                        <span style={{ color: totalLiquidoAdicional >= 0 ? '#27ae60' : '#c0392b', fontWeight: '700' }}>
                          {totalLiquidoAdicional >= 0 ? '+ ' : '− '}{formatarMoeda(Math.abs(totalLiquidoAdicional))}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div style={styles.modalFooterItens}>
                <button onClick={() => setModalItensAdicionaisAberto(false)} style={styles.secondaryButton}>
                  Cancelar
                </button>
                <button onClick={() => gerarRelatorioCliente(itensAdicionais)} style={styles.primaryButton}>
                  <FaDownload /> GERAR RELATÓRIO
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════════
            MODAL DE PESQUISA DE CLIENTE — igual ao da Ordem de Serviço:
            busca por nome/CPF/CNPJ, bloqueia seleção sem documento e oferece
            o botão de editar ao lado do "Selecionar".
        ══════════════════════════════════════════════════════════════════════ */}
        {modalPesquisaAberto && (
          <div style={styles.modalOverlay} className="rel-overlay">
            <div style={styles.modal}>
              <div style={styles.modalHeader}>
                <h2 style={styles.modalTitle}><FaSearch style={{ marginRight: 8 }} /> Buscar Cliente</h2>
                <button onClick={() => { setModalPesquisaAberto(false); setTermoPesquisa(''); setResultadosPesquisa([]); }} style={styles.modalClose}>&times;</button>
              </div>
              <div style={styles.modalContent}>
                <input
                  type="text"
                  value={termoPesquisa}
                  onChange={(e) => setTermoPesquisa(e.target.value)}
                  placeholder="Nome, CPF ou CNPJ..."
                  style={styles.searchInput}
                  onKeyPress={(e) => e.key === 'Enter' && pesquisarClientes()}
                  autoFocus
                />
                <p style={{ fontSize: 11, color: getBuscaHint().color, margin: '4px 0 12px', fontWeight: 500 }}>{getBuscaHint().text}</p>
                <div style={{ marginBottom: 12 }}>
                  <button onClick={pesquisarClientes} style={styles.primaryButton} disabled={pesquisandoCliente}>
                    {pesquisandoCliente ? 'Pesquisando...' : <><FaSearch /> Pesquisar</>}
                  </button>
                </div>

                {pesquisandoCliente ? (
                  <div style={styles.loading}>Carregando...</div>
                ) : resultadosPesquisa.length > 0 ? (
                  <div style={{ maxHeight: 340, overflowY: 'auto' }}>
                    {resultadosPesquisa.map(cliente => {
                      const doc = formatarDocumentoCliente(cliente);
                      const semDocumento = !doc;
                      return (
                        <div key={cliente.idCliente} style={styles.clienteResultItem}>
                          <div style={styles.clienteResultAvatar}>{(cliente.nome || '?')[0].toUpperCase()}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={styles.clienteResultNome}>{cliente.nome}</div>
                            <div style={styles.clienteResultMeta}>
                              {cliente.telefone && <span>{cliente.telefone}</span>}
                              {doc && <span style={styles.docBadge}>{doc}</span>}
                              {semDocumento && (
                                <span style={styles.docBadgeAlerta}>
                                  <FaExclamationTriangle size={9} /> CPF/CNPJ obrigatório
                                </span>
                              )}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                            <button onClick={() => abrirModalEdicaoCliente(cliente)} style={styles.btnEditarCliente} title="Editar dados do cliente">
                              <FaEdit size={11} />
                            </button>
                            {/* ── Sem CPF/CNPJ cadastrado, "Selecionar" fica bloqueado — mesma
                                regra da Ordem de Serviço. Editar continua liberado; assim que
                                o CPF/CNPJ for salvo, este botão habilita sozinho, porque `doc`
                                é recalculado a cada render. ── */}
                            <button
                              onClick={() => selecionarCliente(cliente)}
                              style={{ ...styles.smallButton, ...(semDocumento ? styles.smallButtonDisabled : {}) }}
                              disabled={semDocumento}
                              title={semDocumento ? 'Cadastre o CPF ou CNPJ deste cliente antes de selecionar' : undefined}
                            >
                              Selecionar
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : termoPesquisa ? (
                  <div style={styles.noResults}>Nenhum cliente encontrado</div>
                ) : (
                  <div style={styles.noResults}>Digite nome, CPF ou CNPJ para pesquisar</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Modal Edição de Cliente — igual ao da Ordem de Serviço ── */}
        {modalEdicaoClienteAberto && (
          <div style={styles.modalOverlay} className="rel-overlay">
            <div style={{ ...styles.modal, maxWidth: 700, maxHeight: '90vh' }}>
              <div style={{ ...styles.modalHeader, background: 'linear-gradient(135deg, #004d4d 0%, #008080 100%)' }}>
                <h2 style={{ ...styles.modalTitle, color: '#fff' }}><FaEdit style={{ marginRight: 8 }} /> Editar Dados do Cliente</h2>
                <button onClick={fecharModalEdicaoCliente} style={{ ...styles.modalClose, color: '#fff' }}><FaTimes /></button>
              </div>
              <div style={{ ...styles.modalContent, overflowY: 'auto' }}>
                <ImportarPrintCliente valoresAtuais={formEdicao} onDadosLidos={aplicarPrintEdicao} disabled={salvandoEdicao} />
                <p style={styles.sectionLabel}><FaUser size={11} /> Dados Pessoais</p>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Nome *</label>
                  <input type="text" value={formEdicao.nome} onChange={(e) => setFormEdicao({...formEdicao, nome: e.target.value})} placeholder="Nome completo" style={{ ...styles.input, ...destaquePrintEdicao('nome') }} autoFocus />
                  {errorsEdicao.nome && <span style={styles.errorText}>{errorsEdicao.nome}</span>}
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>RG / Identidade</label>
                  <input type="text" value={formEdicao.identidade} onChange={(e) => setFormEdicao({...formEdicao, identidade: e.target.value.toUpperCase()})} placeholder="Número do documento" style={styles.input} />
                  <small style={styles.hint}>Usado nos requerimentos do DETRAN</small>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>E-mail</label>
                  <input type="email" value={formEdicao.email} onChange={(e) => setFormEdicao({...formEdicao, email: e.target.value})} placeholder="exemplo@email.com" style={{ ...styles.input, ...destaquePrintEdicao('email') }} />
                  {errorsEdicao.email && <span style={styles.errorText}>{errorsEdicao.email}</span>}
                </div>
                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>CPF</label>
                    <div style={{ position: 'relative' }}>
                      <InputMask mask="999.999.999-99" value={formEdicao.cpf} onChange={handleCpfChangeEdicao} placeholder="000.000.000-00" style={{ ...styles.input, ...destaquePrintEdicao('cpf'), ...getInputBorderStyle(cpfStatusEdicao, errorsEdicao.cpf) }} />
                      {cpfStatusEdicao === 'checking' && <FaSpinner style={styles.inputIcon} className="rel-spin" />}
                      {cpfStatusEdicao === 'valid' && <FaCheckCircle style={{ ...styles.inputIcon, color: '#27ae60' }} />}
                      {(cpfStatusEdicao === 'invalid' || cpfStatusEdicao === 'duplicate') && <FaTimesCircle style={{ ...styles.inputIcon, color: '#e74c3c' }} />}
                    </div>
                    {cpfMessageEdicao && <small style={{ fontSize: 11, color: cpfStatusEdicao === 'valid' ? '#27ae60' : cpfStatusEdicao === 'checking' ? '#f39c12' : '#e74c3c' }}>{cpfMessageEdicao}</small>}
                    {errorsEdicao.cpf && <span style={styles.errorText}>{errorsEdicao.cpf}</span>}
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>CNPJ</label>
                    <div style={styles.cnpjRow}>
                      <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
                        <InputMask mask="99.999.999/9999-99" value={formEdicao.cnpj} onChange={handleCnpjChangeEdicao} placeholder="00.000.000/0000-00" style={{ ...styles.input, ...destaquePrintEdicao('cnpj'), ...getInputBorderStyle(cnpjStatusEdicao, errorsEdicao.cnpj) }} />
                        {cnpjStatusEdicao === 'checking' && <FaSpinner style={styles.inputIcon} className="rel-spin" />}
                        {cnpjStatusEdicao === 'valid' && <FaCheckCircle style={{ ...styles.inputIcon, color: '#27ae60' }} />}
                        {(cnpjStatusEdicao === 'invalid' || cnpjStatusEdicao === 'duplicate') && <FaTimesCircle style={{ ...styles.inputIcon, color: '#e74c3c' }} />}
                      </div>
                      <button
                        type="button"
                        onClick={preencherPorCnpjEdicao}
                        disabled={!podeConsultarCnpjEdicao}
                        title={podeConsultarCnpjEdicao ? 'Completar os campos em branco com os dados do CNPJ (Receita Federal)' : 'Digite um CNPJ válido para buscar os dados'}
                        style={{ ...styles.cnpjButton, ...(!podeConsultarCnpjEdicao && styles.cnpjButtonDisabled) }}
                      >
                        {buscandoCnpjEdicao ? <FaSpinner className="rel-spin" /> : <FaSearch />}
                      </button>
                    </div>
                    {buscandoCnpjEdicao && <small style={styles.hint}>Consultando CNPJ...</small>}
                    {cnpjMessageEdicao && <small style={{ fontSize: 11, color: cnpjStatusEdicao === 'valid' ? '#27ae60' : cnpjStatusEdicao === 'checking' ? '#f39c12' : '#e74c3c' }}>{cnpjMessageEdicao}</small>}
                    {errorsEdicao.cnpj && <span style={styles.errorText}>{errorsEdicao.cnpj}</span>}
                  </div>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Telefone</label>
                  <InputMask mask="(99) 99999-9999" maskChar={null} value={formEdicao.telefone} onChange={(e) => setFormEdicao({...formEdicao, telefone: e.target.value})} placeholder="(00) 00000-0000" style={{ ...styles.input, ...destaquePrintEdicao('telefone') }} />
                </div>
                <p style={{ ...styles.sectionLabel, marginTop: 16 }}><FaMapMarkerAlt size={11} /> Endereço</p>
                <div style={styles.formRow}>
                  <div style={{ ...styles.formGroup, flex: 1 }}>
                    <label style={styles.label}>CEP</label>
                    <InputMask mask="99999-999" value={formEdicao.cep} onChange={(e) => setFormEdicao({...formEdicao, cep: e.target.value})} onBlur={(e) => { if (cepVeioDoPrint(e.target.value, cepDoPrintEdicao)) return; const c = e.target.value.replace(/\D/g,''); if (c.length === 8) buscarEnderecoPorCEPEdicao(e.target.value); }} placeholder="00000-000" style={{ ...styles.input, ...destaquePrintEdicao('cep') }} />
                    <small style={styles.hint}>{loadingCepEdicao ? 'Buscando endereço...' : 'Preenche o endereço automaticamente'}</small>
                  </div>
                  <div style={{ ...styles.formGroup, flex: 2 }}>
                    <label style={styles.label}>Cidade</label>
                    <input type="text" value={formEdicao.cidade} onChange={(e) => setFormEdicao({...formEdicao, cidade: e.target.value})} placeholder="Cidade" style={{ ...styles.input, ...destaquePrintEdicao('cidade') }} />
                  </div>
                  <div style={{ ...styles.formGroup, flex: 0.6 }}>
                    <label style={styles.label}>UF</label>
                    <input type="text" value={formEdicao.uf} onChange={(e) => setFormEdicao({...formEdicao, uf: e.target.value.toUpperCase()})} placeholder="UF" maxLength="2" style={{ ...styles.input, ...destaquePrintEdicao('uf'), textTransform: 'uppercase' }} />
                  </div>
                </div>
                <div style={styles.formRow}>
                  <div style={{ ...styles.formGroup, flex: 3 }}>
                    <label style={styles.label}>Logradouro</label>
                    <input type="text" value={formEdicao.logradouro} onChange={(e) => setFormEdicao({...formEdicao, logradouro: e.target.value})} placeholder="Rua, Avenida..." style={{ ...styles.input, ...destaquePrintEdicao('logradouro') }} />
                  </div>
                  <div style={{ ...styles.formGroup, flex: 1 }}>
                    <label style={styles.label}>Número</label>
                    <input type="text" value={formEdicao.numero} onChange={(e) => setFormEdicao({...formEdicao, numero: e.target.value})} placeholder="Nº" style={{ ...styles.input, ...destaquePrintEdicao('numero') }} />
                  </div>
                </div>
                <div style={styles.formRow}>
                  <div style={{ ...styles.formGroup, flex: 1 }}>
                    <label style={styles.label}>Bairro</label>
                    <input type="text" value={formEdicao.bairro} onChange={(e) => setFormEdicao({...formEdicao, bairro: e.target.value})} placeholder="Bairro" style={{ ...styles.input, ...destaquePrintEdicao('bairro') }} />
                  </div>
                  <div style={{ ...styles.formGroup, flex: 1 }}>
                    <label style={styles.label}>Complemento</label>
                    <input type="text" value={formEdicao.complemento} onChange={(e) => setFormEdicao({...formEdicao, complemento: e.target.value})} placeholder="Apto, sala..." style={{ ...styles.input, ...destaquePrintEdicao('complemento') }} />
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
                  <button onClick={fecharModalEdicaoCliente} style={styles.secondaryButton}><FaTimes size={11} /> Cancelar</button>
                  <button onClick={salvarEdicaoCliente} style={styles.primaryButton} disabled={salvandoEdicao || !formEdicao.nome}><FaSave size={11} /> {salvandoEdicao ? 'Salvando...' : 'Salvar Alterações'}</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  // ── Layout base ───────────────────────────────────────────────────────────
  container: { display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#f5f5f5', fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" },
  content: { padding: '24px', maxWidth: '1200px', margin: '0 auto', width: '100%' },
  header: { marginBottom: '24px' },
  titleContainer: { display: 'flex', alignItems: 'center', gap: '12px' },
  titleIcon: { fontSize: '28px', color: '#2ecc71' },
  title: { fontSize: '24px', margin: 0, color: '#333' },

  // ── Tipo de relatório ─────────────────────────────────────────────────────
  tipoContainer: { display: 'grid', gap: '20px', marginBottom: '30px' },
  tipoOption: { backgroundColor: 'white', padding: '20px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '15px', cursor: 'pointer', border: '2px solid transparent', transition: 'all 0.3s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' },
  tipoOptionActive: { borderColor: '#2ecc71', backgroundColor: '#f0fff4' },
  tipoIcon: { fontSize: '32px', color: '#2ecc71' },
  tipoTitle: { margin: '0 0 5px 0', fontSize: '16px', fontWeight: '600' },
  tipoDescription: { margin: 0, fontSize: '13px', color: '#666' },

  // ── Formulários ───────────────────────────────────────────────────────────
  formContainer: { backgroundColor: 'white', borderRadius: '8px', padding: '24px', marginBottom: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' },
  formHeader: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', paddingBottom: '15px', borderBottom: '1px solid #eee' },
  formIcon: { fontSize: '20px', color: '#2ecc71' },
  formTitle: { margin: 0, fontSize: '18px', fontWeight: '600', color: '#333' },
  formRow: { display: 'flex', gap: '20px', marginBottom: '20px', flexWrap: 'wrap' },
  inputGroup: { flex: '1', minWidth: '200px' },
  label: { display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: '#34495e' },
  input: { width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' },
  hintText: { display: 'block', fontSize: '12px', color: '#7f8c8d', marginTop: '4px' },
  selecionarClienteButton: { padding: '10px 16px', backgroundColor: '#3498db', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '14px' },
  clienteSelecionado: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', backgroundColor: '#f0fff4', border: '1px solid #2ecc71', borderRadius: '4px' },
  clienteInfo: { display: 'flex', flexDirection: 'column', gap: '4px' },
  clienteDocumento: { fontSize: '12px', color: '#666' },
  removerClienteButton: { padding: '6px 12px', backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' },
  infoBox: { backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '6px', display: 'flex', gap: '15px', marginTop: '15px', flexWrap: 'wrap' },
  infoIcon: { fontSize: '24px', color: '#3498db' },
  infoList: { margin: '8px 0 0 0', paddingLeft: '20px', fontSize: '13px', color: '#555' },
  resumoProdutosSelecionados: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' },
  produtoResumo: { backgroundColor: '#e8f5e9', padding: '4px 10px', borderRadius: '16px', fontSize: '12px', color: '#2e7d32' },
  semProdutosMsg: { margin: '10px 0 0 0', fontSize: '13px', color: '#e74c3c' },
  gerarButton: { width: '100%', padding: '14px', backgroundColor: '#2ecc71', color: 'white', border: 'none', borderRadius: '6px', fontSize: '16px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'background-color 0.3s' },
  spinner: { animation: 'spin 1s linear infinite' },

  // ── Overlay ───────────────────────────────────────────────────────────────
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '24px 16px' },

  // ══════════════════════════════════════════════════════════════════════════
  // MODAL MENSAL — REESTILIZADO
  // ══════════════════════════════════════════════════════════════════════════
  modalMensal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '860px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
    overflow: 'hidden',
  },

  modalMensalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '20px 24px',
    borderBottom: '2px solid #f0f0f0',
    backgroundColor: '#fafafa',
  },
  modalMensalHeaderLeft: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '14px',
  },
  modalMensalTitle: {
    margin: '0 0 4px 0',
    fontSize: '17px',
    fontWeight: '700',
    color: '#2c3e50',
  },
  modalMensalSubtitle: {
    margin: 0,
    fontSize: '13px',
    color: '#666',
    fontWeight: 'normal',
  },
  modalMensalClose: {
    background: 'none',
    border: '1px solid #ddd',
    borderRadius: '6px',
    padding: '6px 8px',
    cursor: 'pointer',
    color: '#888',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
    flexShrink: 0,
  },

  modalMensalBody: {
    display: 'grid',
    gridTemplateColumns: '1fr 56px 1fr',
    gap: '0',
    flex: 1,
    minHeight: 0,
    padding: '20px 24px',
    alignItems: 'stretch',
  },

  // ── Painéis laterais ──────────────────────────────────────────────────────
  painel: {
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: 'white',
    minWidth: 0, // permite que o painel encolha dentro do grid sem forçar overflow horizontal
  },
  painelHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 14px',
    backgroundColor: '#f8f8f8',
    borderBottom: '1px solid #e8e8e8',
  },
  painelTitle: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#444',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
  painelContador: {
    backgroundColor: '#bbb',
    color: 'white',
    borderRadius: '20px',
    padding: '2px 8px',
    fontSize: '12px',
    fontWeight: '700',
    minWidth: '24px',
    textAlign: 'center',
    transition: 'background-color 0.3s',
  },
  painelBusca: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    borderBottom: '1px solid #f0f0f0',
    backgroundColor: '#fdfdfd',
  },
  painelBuscaIcon: { color: '#aaa', fontSize: '12px', flexShrink: 0 },
  painelBuscaInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    fontSize: '13px',
    backgroundColor: 'transparent',
    color: '#333',
  },
  painelBuscaClear: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#aaa',
    padding: '2px',
    display: 'flex',
    alignItems: 'center',
  },
  painelLista: {
    flex: 1,
    overflowY: 'auto',
    padding: '6px',
    minHeight: '300px',
    maxHeight: '380px',
  },
  painelItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 10px',
    marginBottom: '3px',
    borderRadius: '5px',
    border: '1px solid #e8e8e8',
    cursor: 'pointer',
    transition: 'all 0.15s',
    userSelect: 'none',
    fontSize: '13px',
    color: '#333',
    gap: '8px',
  },
  painelItemNome: {
    flex: 1,
    lineHeight: '1.3',
  },
  painelItemCheck: {
    backgroundColor: '#2ecc71',
    color: 'white',
    borderRadius: '50%',
    width: '18px',
    height: '18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '10px',
    fontWeight: '700',
    flexShrink: 0,
  },
  painelVazio: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    height: '100%',
    minHeight: '200px',
    color: '#bbb',
    padding: '20px',
  },

  // ── Controles centrais ────────────────────────────────────────────────────
  controlesCentro: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '0 8px',
  },
  ctrlBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    border: 'none',
    borderRadius: '6px',
    padding: '9px 12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    width: '40px',
    fontWeight: '600',
    fontSize: '12px',
  },
  ctrlBtnActive: {
    backgroundColor: '#2ecc71',
    color: 'white',
    boxShadow: '0 2px 6px rgba(46,204,113,0.35)',
  },
  ctrlBtnDanger: {
    backgroundColor: '#e74c3c',
    color: 'white',
    boxShadow: '0 2px 6px rgba(231,76,60,0.3)',
  },
  ctrlBtnDisabled: {
    backgroundColor: '#f0f0f0',
    color: '#ccc',
    cursor: 'not-allowed',
  },
  ctrlDivisor: {
    width: '28px',
    height: '1px',
    backgroundColor: '#e0e0e0',
    margin: '2px 0',
  },

  // ── Dica e rodapé ─────────────────────────────────────────────────────────
  modalMensalDica: {
    padding: '10px 24px',
    fontSize: '12px',
    color: '#e67e22',
    backgroundColor: '#fffbf2',
    borderTop: '1px solid #fdebd0',
    textAlign: 'center',
  },
  modalMensalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 24px',
    borderTop: '1px solid #eee',
    backgroundColor: '#f8f9fa',
  },

  // ── Modal itens adicionais ─────────────────────────────────────────────────
  modalItens: { backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '680px', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 30px rgba(0,0,0,0.3)' },
  modalSubtitle: { margin: '4px 0 0 0', fontSize: '13px', color: '#555', fontWeight: 'normal' },
  modalBodyItens: { padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' },
  instrucaoTexto: { margin: '0 0 4px 0', fontSize: '13px', color: '#666', lineHeight: '1.5' },
  modalFooterItens: { display: 'flex', justifyContent: 'flex-end', gap: '12px', padding: '16px 24px', borderTop: '1px solid #eee', backgroundColor: '#f8f9fa', borderRadius: '0 0 12px 12px' },

  // ── Ações do cabeçalho do modal (engrenagem + fechar) ───────────────────────
  modalHeaderActions: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    flexShrink: 0,
  },
  modalConfigBtn: {
    background: 'none',
    border: '1px solid #ddd',
    borderRadius: '6px',
    padding: '6px 9px',
    cursor: 'pointer',
    color: '#666',
    fontSize: '15px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
  modalConfigBtnAtivo: {
    backgroundColor: '#eafaf1',
    borderColor: '#2ecc71',
    color: '#27ae60',
  },
  configPainel: {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    right: 0,
    backgroundColor: 'white',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
    padding: '12px 14px',
    minWidth: '260px',
    zIndex: 1100,
  },
  configPainelTitle: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '8px',
  },
  configPainelItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: '#333',
    cursor: 'pointer',
  },
  configPainelCheckbox: {
    width: '15px',
    height: '15px',
    cursor: 'pointer',
  },

  // ── Item adicional ─────────────────────────────────────────────────────────
  itemAdicional: { borderRadius: '8px', overflow: 'hidden', border: '1px solid #e0e0e0', transition: 'all 0.2s ease' },
  itemAdicionalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', cursor: 'pointer', userSelect: 'none' },
  itemAdicionalCheck: { display: 'flex', alignItems: 'center', gap: '12px' },
  itemAdicionalLabel: { display: 'block', fontWeight: '600', fontSize: '14px' },
  itemAdicionalTipo: { display: 'inline-block', marginTop: '4px', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '700', letterSpacing: '0.5px' },
  itemAdicionalTotalBadge: { padding: '4px 12px', borderRadius: '20px', color: 'white', fontSize: '13px', fontWeight: '700', whiteSpace: 'nowrap' },
  itemAdicionalCampos: { padding: '0 16px 20px 16px', borderTop: '1px solid rgba(0,0,0,0.06)' },
  campoLabel: { fontSize: '12px', fontWeight: '600', color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px' },
  totalCalculado: { padding: '9px 16px', border: '1px solid', borderRadius: '4px', fontSize: '15px', fontWeight: '700', backgroundColor: 'white', minWidth: '120px', textAlign: 'center' },

  // ── NOVO: indicador discreto enquanto os dados são buscados automaticamente
  // (sem botão nenhum — aparece assim que o checkbox é marcado) ───────────────
  importCarregandoBox: {
    display: 'flex', alignItems: 'center', gap: '8px', marginTop: '14px',
    fontSize: '13px', color: '#888', fontStyle: 'italic',
  },

  // ── Resumo importado somente leitura ────────────────────────────────────
  importResumoBox: {
    display: 'flex', gap: '16px', marginTop: '14px', flexWrap: 'wrap', alignItems: 'flex-end',
  },
  importResumoItem: { display: 'flex', flexDirection: 'column', gap: '6px' },
  importResumoValor: {
    padding: '9px 14px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px',
    fontWeight: '600', backgroundColor: '#f8f9fa', minWidth: '90px', textAlign: 'center', color: '#333',
  },

  // ── Detalhamento por lançamento (ex: vistorias com preços diferentes por
  // veículo/distância no mesmo período, procurações com placas diferentes) —
  // caixa discreta, fonte pequena, abaixo do resumo principal do item ─────
  detalhamentoBox: {
    marginTop: '12px',
    backgroundColor: '#ffffff',
    border: '1px dashed #ccc',
    borderRadius: '6px',
    padding: '8px 12px',
  },
  detalhamentoTitle: {
    display: 'block',
    fontSize: '10.5px',
    fontWeight: '700',
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    marginBottom: '6px',
  },
  detalhamentoLinhas: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    maxHeight: '220px',
    overflowY: 'auto',
  },
  detalhamentoLinha: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '10px',
    fontSize: '11.5px',
    color: '#555',
    padding: '2px 0',
    borderBottom: '1px dotted #eee',
  },
  detalhamentoLinhaEsquerda: {
    color: '#666',
  },
  detalhamentoLinhaValor: {
    fontWeight: '600',
    whiteSpace: 'nowrap',
  },

  // ── Resumo ajuste ─────────────────────────────────────────────────────────
  resumoAjuste: { backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e0e0e0', overflow: 'hidden', marginTop: '4px', marginBottom: '4px' },
  resumoAjusteTitle: { padding: '10px 16px', fontWeight: '700', fontSize: '13px', color: '#555', borderBottom: '1px solid #e0e0e0', textTransform: 'uppercase', letterSpacing: '0.5px' },
  resumoAjusteLinhas: { padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '8px' },
  resumoLinha: { display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#444' },
  resumoLinhaTotalAjuste: { display: 'flex', justifyContent: 'space-between', fontSize: '14px', fontWeight: '600', paddingTop: '8px', borderTop: '1px solid #e0e0e0', color: '#333' },

  // ── Modal pesquisa cliente ─────────────────────────────────────────────────
  modal: { backgroundColor: 'white', borderRadius: '8px', width: '100%', maxWidth: '700px', maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: '1px solid #eee', backgroundColor: '#f8f9fa', flexShrink: 0 },
  modalTitle: { margin: 0, fontSize: '18px', fontWeight: '600', display: 'flex', alignItems: 'center' },
  modalClose: { background: 'none', border: 'none', fontSize: '28px', cursor: 'pointer', color: '#999' },
  modalContent: { padding: '16px', flex: 1, overflowY: 'auto' },
  searchBox: { display: 'flex', gap: '10px', marginBottom: '20px' },
  searchInput: { width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' },
  loading: { textAlign: 'center', padding: '20px', color: '#666' },
  resultsTable: { maxHeight: '400px', overflowY: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
  th: { backgroundColor: '#f8f9fa', padding: '10px', textAlign: 'left', borderBottom: '1px solid #ddd', position: 'sticky', top: 0 },
  tr: { borderBottom: '1px solid #eee' },
  td: { padding: '10px' },
  smallButton: { padding: '8px 14px', backgroundColor: '#2ecc71', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' },
  smallButtonDisabled: { backgroundColor: '#bdc3c7', cursor: 'not-allowed' },
  noResults: { textAlign: 'center', padding: '20px', color: '#666' },

  // ── NOVO: itens de resultado de busca (avatar, meta, badges) — igual à Ordem de Serviço ──
  clienteResultItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 4px', borderBottom: '1px solid #f5f5f5' },
  clienteResultAvatar: { width: 34, height: 34, minWidth: 34, borderRadius: '50%', background: 'linear-gradient(135deg, #008080, #20b2aa)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, flexShrink: 0 },
  clienteResultNome: { fontWeight: 600, fontSize: 13, color: '#1a2e35', marginBottom: 2 },
  clienteResultMeta: { display: 'flex', gap: 10, fontSize: 11, color: '#999', alignItems: 'center', flexWrap: 'wrap' },
  docBadge: { background: '#e8f8f5', color: '#008080', border: '1px solid #b2e0da', borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 600 },
  docBadgeAlerta: { display: 'inline-flex', alignItems: 'center', gap: 4, background: '#fff5eb', color: '#e07030', border: '1px solid #ffd8b0', borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 600 },
  btnEditarCliente: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 12px', background: 'linear-gradient(135deg, #e07030, #f0a060)', color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 600 },

  // ── NOVO: campos do formulário de edição de cliente — igual à Ordem de Serviço ──
  formGroup: { display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 12 },
  sectionLabel: { fontSize: 11, fontWeight: 700, color: '#008080', textTransform: 'uppercase', letterSpacing: 0.6, display: 'flex', alignItems: 'center', gap: 6, margin: '0 0 10px', paddingBottom: 6, borderBottom: '1px solid #f0f0f0' },
  inputIcon: { position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#aaa' },
  errorText: { fontSize: 11, color: '#e74c3c', marginTop: 2, display: 'block' },
  hint: { fontSize: 10, color: '#aaa', fontStyle: 'italic' },
  // Lupa do CNPJ ao lado do campo (mesmo visual da Ordem de Serviço)
  cnpjRow: { display: 'flex', gap: 6, alignItems: 'stretch' },
  cnpjButton: { backgroundColor: '#3498db', color: '#fff', border: 'none', padding: '0 14px', borderRadius: 6, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center' },
  cnpjButtonDisabled: { backgroundColor: '#b8c2cc', cursor: 'not-allowed' },

  // ── Botões principais ─────────────────────────────────────────────────────
  primaryButton: { padding: '10px 20px', backgroundColor: '#2ecc71', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' },
  secondaryButton: { padding: '10px 20px', backgroundColor: '#95a5a6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' },
};

const styleSheet = document.createElement("style");
styleSheet.textContent = `
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes relSpin { from { transform: translateY(-50%) rotate(0deg); } to { transform: translateY(-50%) rotate(360deg); } }
.rel-spin { animation: relSpin 1s linear infinite; }

/* ════════════════════════════════════════════════════════════════════════
   RESPONSIVIDADE DOS MODAIS — Relatório Mensal e Itens Adicionais
   Cobre: monitores pequenos, zoom de página alto (125%–200%), mobile/tablet.
   Os estilos inline definem o "desktop padrão"; aqui sobrescrevemos via
   media query real para telas estreitas e alturas reduzidas.
════════════════════════════════════════════════════════════════════════ */

/* ── Overlay: nunca deixa o modal colado nas bordas em telas pequenas ── */
.rel-overlay {
  padding: 16px !important;
}

/* ── MODAL MENSAL ──────────────────────────────────────────────────────── */

/* Altura do modal flexível: nunca mais que 92% da viewport, mesmo com zoom */
.rel-modal-mensal {
  max-height: 92vh !important;
}

/* Lista de produtos: altura fluida em vez de fixa em px.
   Cresce com a tela, mas sempre deixa espaço para cabeçalho/rodapé. */
.rel-painel-lista {
  max-height: min(380px, 45vh) !important;
  min-height: 180px !important;
}

/* Abaixo de 900px (tablets em retrato e zoom alto em monitores pequenos):
   empilha os dois painéis e os controles centrais ficam horizontais entre eles */
@media (max-width: 900px), (max-height: 700px) {
  .rel-modal-mensal-body {
    grid-template-columns: 1fr !important;
    grid-template-rows: auto auto auto !important;
    gap: 12px !important;
    padding: 16px !important;
    overflow-y: auto !important;
  }
  .rel-controles-centro {
    flex-direction: row !important;
    justify-content: center !important;
    padding: 4px 0 !important;
  }
  .rel-painel-lista {
    max-height: 240px !important;
    min-height: 140px !important;
  }
  .rel-modal-mensal-header {
    padding: 16px 18px !important;
  }
}

/* Mobile estreito: ajustes finos de espaçamento e fonte */
@media (max-width: 480px) {
  .rel-modal-mensal-header h2 {
    font-size: 15px !important;
  }
  .rel-painel-lista {
    max-height: 200px !important;
  }
}

/* ── MODAL ITENS ADICIONAIS ───────────────────────────────────────────── */

.rel-modal-itens {
  max-height: 92vh !important;
  overflow-y: auto !important;
}

/* Linha de resumo importado (Quantidade / Valor Unitário / Total):
   empilha em telas estreitas e em zoom alto, em vez de espremer lado a lado */
@media (max-width: 640px), (max-height: 650px) {
  .rel-item-row {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 10px !important;
  }
  .rel-campo-grupo {
    width: 100% !important;
  }
  .rel-total-calculado {
    width: 100% !important;
    box-sizing: border-box !important;
  }
}

/* ── Zoom de página muito alto (>150%) em qualquer monitor:
   garante que nenhum modal jamais ultrapasse a viewport visível ── */
@media (max-height: 550px) {
  .rel-modal-mensal,
  .rel-modal-itens {
    max-height: 96vh !important;
  }
  .rel-painel-lista {
    max-height: 120px !important;
    min-height: 90px !important;
  }
}
`;
document.head.appendChild(styleSheet);

export default Relatorios;
